import React, { useEffect, useState } from 'react';
import { Layout, message, Button, Form, Input, Switch, Select, Tabs, Drawer, InputNumber } from 'antd';
import { PageHeader } from '@ant-design/pro-components';
import { useNavigate, useParams } from 'react-router-dom';
import MemberSidebar from './components/member.sidebar.component';
import MemberFooter from './components/member.footer.component';
import { useDispatch, useSelector } from 'react-redux';
import dot from 'dot-object';
import MediaSelect from './components/mediaSelect.field';
import EntityMedia from './entity.media.page';
import { API_CDN_URL } from '../services/config';
import {
    readTripRegion,
    createTripRegion,
    updateTripRegion
} from '../actions/entity.trip.region'
import {
    readAllActiveTripCountriesTranslations
} from '../actions/entity.trip.country';
import { userIsReadOnlyInEntity } from '../helpers/user';
import { useTranslation } from 'react-i18next';
import SeoFields from './components/seo.component';

const layout = {
    labelCol: {
        span: 8,
    },
    wrapperCol: {
        span: 10,
    },
};

function TripRegion() {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const dispatch = useDispatch();
    const params = useParams();
    const entities = useSelector(state => state.entity.entities);
    const currentEntity = entities ? entities.find(entity => entity.id === params.entityId) : null;
    const { Content } = Layout;
    const [form] = Form.useForm();
    const [tripRegionTitle, setTripRegionTitle] = useState('');
    const currentUser = useSelector(state => state.auth.user);
    const userIsReadOnly = userIsReadOnlyInEntity(currentUser, currentEntity);
    const requiredTripSupportAllLanguages = currentEntity && currentEntity.requiredTripSupportAllLanguages;
    const [addedLanguages, setAddedLanguages] = useState([]);
    const [mediaDrawerVisible, setMediaDrawerVisible] = useState(false);
    const [currentMediaFieldName, setCurrentMediaFieldName] = useState(null);
    const [regionData, setRegionData] = useState(null);
    const [countryOptions, setCountryOptions] = useState([]);

    const showMediaDrawer = (fieldName) => {
        setCurrentMediaFieldName(fieldName);
        setMediaDrawerVisible(true);
    };
    const onMediaDrawerClose = () => {
        setMediaDrawerVisible(false);
    };

    const onSelectMedia = (media) => {
        setMediaDrawerVisible(false);
        if (currentMediaFieldName && currentEntity) {
            let url = API_CDN_URL(currentEntity) + media.path;
            let newValue = {
                [currentMediaFieldName.join('.')]: url
            };
            newValue = dot.object(newValue);
            form.setFieldsValue(newValue);
        }
    }

    useEffect(() => {
        if (currentEntity) {
            if (currentEntity.requiredTripSupportAllLanguages) {
                setAddedLanguages(currentEntity.languages.filter(language => {
                    return language.status
                }).map(language => {
                    return {
                        label: language.name,
                        value: language.code
                    }
                }));
            } else {
                if (regionData && typeof regionData.languages !== 'undefined' && Array.isArray(regionData.languages)) {
                    setAddedLanguages(currentEntity.languages.filter(language => {
                        return language.status && regionData.languages.includes(language.code)
                    }).map(language => {
                        return {
                            label: language.name,
                            value: language.code
                        }
                    }));
                }
            }
            // Fetch the countries
            dispatch(readAllActiveTripCountriesTranslations(currentEntity.id)).then((response) => {
                if (typeof response.result !== 'undefined') {
                    setCountryOptions(response.result.map(countryTranslation => {
                        return {
                            label: countryTranslation.name,
                            value: countryTranslation.id,
                            language: countryTranslation.language
                        }
                    }));
                }
            }).catch(error => {
                console.log(error);
                message.error(error.message);
            });
        }
    }, [currentEntity, regionData]);
    const languageOptions = !requiredTripSupportAllLanguages && currentEntity && currentEntity.languages ? currentEntity.languages.map(language => {
        return {
            label: language.name,
            value: language.code,
            disabled: !language.status
        }
    }) : [];

    const onFinish = (values) => {
        if (userIsReadOnly) {
            message.error(t('tripRegion.message.read-only'));
            return;
        }
        if (typeof params.regionId !== 'undefined') {
            // Update the existing trip region
            dispatch(updateTripRegion(params.entityId, params.regionId, values)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripRegionTitle(t('tripRegion.title.edit'));
                    form.setFieldsValue(response.result);
                    setRegionData(response.result);
                }
                message.success(t('tripRegion.message.update-success'));
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        } else {
            // Create new campaign
            dispatch(createTripRegion(params.entityId, values)).then((data) => {
                message.success(t('tripRegion.message.create-success'));
                if (data.result) {
                    navigate(`/entity/${params.entityId}/trip-region/${data.result.id}`);
                } else {
                    navigate(`/entity/${params.entityId}/trip-regions`);
                }
            }).catch(error => {
                try {
                    let responseMessage = error.response.data.message;
                    message.error(responseMessage);
                } catch (e) {
                    console.log('Failed:', e);
                    message.error(JSON.stringify(e));
                }
            });
        }
    }

    useEffect(() => {
        if (typeof params.regionId !== 'undefined') {
            setTripRegionTitle(t('tripRegion.title.edit'));
            dispatch(readTripRegion(params.entityId, params.regionId)).then((response) => {
                if (typeof response.result !== 'undefined' && typeof response.result.id !== 'undefined') {
                    setTripRegionTitle(t('tripRegion.title.edit'));
                    form.setFieldsValue(response.result);
                    setRegionData(response.result);
                } else {
                    message.error(t('tripRegion.message.error-reading'));
                    navigate(`/entity/${params.entityId}/trip-regions`);
                }
            }).catch(e => {
                message.error(t('tripRegion.message.error-reading'));
                navigate(`/entity/${params.entityId}/trip-regions`);
            });
        } else {
            setTripRegionTitle(t('tripRegion.title.new'));
        }
    }, [params.regionId, params.entityId, dispatch, navigate, form]);

    const onFinishFailed = (errorInfo) => {
        if (typeof errorInfo.errorFields !== 'undefined') {
            errorInfo.errorFields.forEach(field => {
                message.error(field.errors[0]);
            });
        }
    }

    const selectedLanguages = (options) => {
        if (Array.isArray(options)) {
            const filteredLanguages = languageOptions.filter(language => {
                if (options.includes(language.value)) {
                    return true;
                } else {
                    return false;
                }
            });
            console.log(options);
            console.log(filteredLanguages);
            setAddedLanguages(filteredLanguages);
        } else {
            setAddedLanguages([]);
        }
    }

    let extraButtons = [
        <Button key="cancel" type="primary" danger href={`/entity/${params.entityId}/trip-regions`}>{t('tripRegion.actions.cancel')}</Button>
    ];
    if (!userIsReadOnly) {
        extraButtons.push(<Button key="save" type="primary" htmlType="submit">{params.regionId ? t('tripRegion.actions.update') : t('tripRegion.actions.save')}</Button>);
    }
    return <>
        <MemberSidebar />
        <Layout className="site-layout">
            <Content style={{ margin: '0 16px' }}>
                <div className="site-layout-background">
                    <Form
                        {...layout}
                        form={form}
                        name="entity-region"
                        onFinish={onFinish}
                        onFinishFailed={onFinishFailed}
                        initialValues={{
                            status: true
                        }}>
                        <PageHeader
                            className="site-page-header"
                            onBack={() => navigate(`/entity/${params.entityId}/trip-regions`)}
                            title={tripRegionTitle}
                            extra={extraButtons}
                        />
                        <>
                            <Form.Item
                                label={t('tripRegion.field.status.label')}
                                name="status"
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripRegion.field.slug.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="slug"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripRegion.field.slug.required')
                                        },
                                        { pattern: /^[a-z0-9-_]+$/, message: t('tripRegion.field.slug.pattern') }
                                    ]}
                                    noStyle>
                                    <Input placeholder={t('tripRegion.field.slug.placeholder')} />
                                </Form.Item>
                                <div>{t('tripRegion.field.slug.note')}</div>
                            </Form.Item>
                            <Form.Item
                                label={t('tripRegion.field.excludeFromMenu.label')}
                                name={'excludeFromMenu'}
                                valuePropName="checked"
                            >
                                <Switch />
                            </Form.Item>
                            <Form.Item
                                label={t('tripRegion.field.order.label')}
                                name={'order'}
                            >
                                <InputNumber />
                            </Form.Item>

                            {!requiredTripSupportAllLanguages && <Form.Item
                                label={t('tripRegion.field.languages.label')}
                                required={true}
                            >
                                <Form.Item
                                    name="languages"
                                    rules={[
                                        {
                                            required: true,
                                            message: t('tripRegion.field.languages.required')
                                        }
                                    ]}
                                    noStyle>
                                    <Select mode="multiple" allowClear options={languageOptions} onChange={selectedLanguages} />
                                </Form.Item>
                                <div>{t('tripRegion.field.languages.note')}</div>
                            </Form.Item>}
                            <Tabs>
                                {addedLanguages.map((language, index) => {
                                    return <Tabs.TabPane tab={language.label} key={language.value} forceRender>
                                        <Form.Item
                                            label={t('tripRegion.field.name.label')}
                                            name={[language.value, 'name']}
                                            required={true}
                                            rules={
                                                [
                                                    {
                                                        required: true,
                                                        message: t('tripRegion.field.name.required')

                                                    }
                                                ]}
                                        >
                                            <Input placeholder={t('tripRegion.field.name.placeholder')} />
                                        </Form.Item>
                                        <Form.Item
                                            label={t('tripRegion.field.subtitle.label')}
                                            name={[language.value, 'subtitle']}
                                        >
                                            <Input placeholder={t('tripRegion.field.subtitle.placeholder')} />
                                            {/* <Input.TextArea autoSize={{ minRows: 5, maxRows: 10 }} placeholder={t('tripRegion.field.description.placeholder')} /> */}
                                        </Form.Item>
                                        <MediaSelect showMediaDrawer={showMediaDrawer} form={form} wrapper={{
                                            wrapperCol: 16,
                                            required: true,
                                            label: t('tripRegion.field.banner.label')
                                        }} inner={{
                                            name: [language.value, 'banner'],
                                            rules: [
                                                {
                                                    required: true,
                                                    message: t('tripRegion.field.banner.required')
                                                }
                                            ]
                                        }} />
                                        <Form.Item
                                            label={t('tripRegion.field.countries.label')}
                                            name={[language.value, 'countries']}
                                        >
                                            <Select mode="multiple" allowClear options={countryOptions.filter(countryTranslation => {
                                                if (countryTranslation.language === language.value) {
                                                    return true;
                                                } else {
                                                    return false;
                                                }
                                            })} />
                                        </Form.Item>
                                        <SeoFields
                                            name={[language.value]}
                                            layout={layout}
                                            form={form}
                                            showMediaDrawer={showMediaDrawer}
                                        />
                                    </Tabs.TabPane>
                                })}
                            </Tabs>
                        </>
                    </Form>
                    <Drawer contentWrapperStyle={{ width: '1000px', maxWidth: '100%', minWidth: '50%' }} title={t('mediaDrawer.title')} placement="right" onClose={onMediaDrawerClose} open={mediaDrawerVisible}>
                        <EntityMedia entityId={params.entityId} selectMedia={onSelectMedia} />
                    </Drawer>
                </div>
            </Content>
            <MemberFooter />
        </Layout>
    </>;
}

export default TripRegion;